import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getArticleBySlug,
  ArticleFull,
} from "../../services/public/publicService";
import ReactMarkdown from "react-markdown";
import { ARTICLE_REMARK_PLUGINS } from "../../utils/articleMarkdown";
import { useSeo } from "../../hooks/useSeo";

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("he-IL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // SEO — title/description/og מתעדכנים כשהמאמר נטען (נתפס ע"י ה-pre-render).
  useSeo(
    article
      ? {
          title: `${article.title} | CarTax`,
          description: article.meta_description || undefined,
          // og:image חייב להיות URL מוחלט; הנתיב ב-DB יחסי
          image: article.cover_image_url
            ? `https://cartax.org${article.cover_image_url}`
            : undefined,
          ogType: "article",
        }
      : {}
  );

  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      const res = await getArticleBySlug(slug);
      if (res.res === "OK" && res.article) {
        setArticle(res.article);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading)
    return (
      <div
        dir="rtl"
        className="min-h-screen flex items-center justify-center text-gray-500"
      >
        טוען מאמר...
      </div>
    );

  if (notFound || !article)
    return (
      <div
        dir="rtl"
        className="min-h-screen flex flex-col items-center justify-center gap-4"
      >
        <h1 className="text-2xl font-bold text-gray-700">המאמר לא נמצא</h1>
        <Link to="/articles" className="text-blue-700 hover:underline">
          חזרה למאמרים
        </Link>
      </div>
    );

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* breadcrumb */}
        <nav className="text-sm text-gray-400 mb-8" aria-label="נתיב ניווט">
          <Link to="/" className="hover:underline">
            דף הבית
          </Link>
          <span className="mx-2">›</span>
          <Link to="/articles" className="hover:underline">
            מאמרים
          </Link>
          <span className="mx-2">›</span>
          <span className="text-gray-600">{article.category_name}</span>
        </nav>

        {/* כותרת */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-blue-900 text-white text-xs font-medium px-3 py-1 rounded-full">
              {article.category_name}
            </span>
            <span className="text-gray-400 text-sm">
              {formatDate(article.published_at)}
            </span>
            <span className="text-gray-400 text-sm">
              ⏱ {article.read_time_minutes} דקות קריאה
            </span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
            {article.title}
          </h1>

          {article.cover_image_url && (
            <img
              src={article.cover_image_url}
              alt={article.title}
              className="w-full rounded-lg mb-8"
              loading="lazy"
            />
          )}

          {/* תוכן WYSIWYG — העיצוב ב-index.css תחת .article-content */}
          <div className="article-content">
            <ReactMarkdown remarkPlugins={ARTICLE_REMARK_PLUGINS}>{article.content}</ReactMarkdown>
          </div>
        </div>

        {/* כפתור חזרה */}
        <div className="mt-8">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-blue-700 hover:underline font-medium"
          >
            ← חזרה לכל המאמרים
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;
